import {from as observableFrom, interval, merge, of as observableOf, Subscription} from 'rxjs';

import {
  catchError,
  concatAll,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  throttle,
  withLatestFrom
} from 'rxjs/operators';
/**
 * Created by christiancueni on 07/07/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */
import clone from 'lodash-es/clone';
import {Injectable, Injector, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';

import * as klassenbuch from '../reducers';
import {AppState, getAuthUser} from '../reducers';
import {BackendEvent, UserData, ViewEvent} from '../models';
import * as klassenbuchActions from '../actions/klassenbuch';
import {
  AddCommentFailAction,
  AddEventFailAction,
  AddEventSuccessAction,
  DateSelectedAction,
  DeleteCommentFailAction,
  DeleteEventAction,
  DeleteEventFailAction,
  DeleteEventSuccessAction,
  GetEventAction,
  GetEventFailAction,
  GetEventFromStorageSuccessAction,
  GetEventsAction,
  GetEventsFailAction,
  GetEventsFromStorageSuccessAction,
  GetEventsSuccessAction,
  GetEventSuccessAction,
  GetMyDocumentsAction,
  GetMyDocumentsFailAction,
  GetMyDocumentsSuccessAction,
  KlassenbuchActionTypes,
  LoadAdminMessagesAction,
  LoadAdminMessagesFailureAction,
  LoadAdminMessagesSuccessAction,
  LoadCommentsAction,
  LoadCommentsFailAction,
  LoadCommentsSuccessAction,
  PublishUpdateEventAction,
  PublishUpdateEventSuccessAction,
  UpdateDocumentOrderFilterAction,
  UpdateDocumentPageAction,
  UpdateDocumentQueryAction,
  UpdateEventFailAction,
  UpdateEventSuccessAction,
  UpdateWeekdaysAction
} from '../actions/klassenbuch';
import {UiHideMenuAction} from '../actions/ui';
import {UpdateGroupSettings} from "../actions/authentication";
import {CalendarService, HelloClassApiService} from '../services';
import {AddToastAction} from "../alerts/alerts.actions";
import {OfflineStorageService} from '../app/offlinestorage.service';

@Injectable()
export class KlassenbuchEffects implements OnDestroy {

  private selectSubscription: Subscription;

  constructor(private actions$: Actions,
              private store: Store<AppState>,
              private calendarService: CalendarService,
              private helloclassApi: HelloClassApiService,
              private injector: Injector,
              private offlineStorageServer: OfflineStorageService) {

    // TODO: Is this correct?
    // don't know if this is the right place (kind of a post reducer action....)

    this.selectSubscription = this.store.select(klassenbuch.getSelectedDisplayGroups).pipe(
      withLatestFrom(this.store.select(klassenbuch.getCalendarDays)))
      .subscribe(values => this.store.dispatch(new GetEventsAction({
        groups: values[0],
        start: values[1][0],
        end: values[1][4]
      })));
  }

  ngOnDestroy() {
    this.selectSubscription.unsubscribe();
  }

  todaySelected$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.TODAY_SELECTED),
    map(() => this.calendarService.getToday()),
    map(today => new DateSelectedAction({date: `${today}`, sameWeek: false})),));

  selectDate$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.DATE_SELECTED),
    filter(action => !action.payload.sameWeek), // only load if it's not the same week
    map(action => action.payload.date),
    map((date) => {
      return {
        selectedDate: date,
        weekdays: this.calendarService.getWeekdaysForDate(date)
      }
    }),
    map(dateUpdate => new UpdateWeekdaysAction(dateUpdate)),));

  getEventsForWeek$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.UpdateWeekdaysAction>(KlassenbuchActionTypes.UPDATE_WEEKDAYS),
    map(action => action.payload['weekdays']), // weekdays needed as payload? use from store
    debounceTime(100),
    withLatestFrom(this.store.select(klassenbuch.getSelectedDisplayGroups), this.store.select(klassenbuch.getGroups)),
    map((values) => {
      // if the user is in no groups don't bother making a api call
      if (values[2].length > 0) {
        return new GetEventsAction({
          groups: values[1],
          start: values[0][0],
          end: values[0][4]
        });
      }
      ;
      return {type: 'NOP', payload: null};
    })
  ));


  addEvent$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.AddEventAction>(KlassenbuchActionTypes.ADD_EVENT),
    switchMap(action => this.helloclassApi.addEvent(action.payload).pipe(
      withLatestFrom(this.store.select(klassenbuch.getBackUrl)),
      tap((values: [any, any]) => {
        let router = this.injector.get(Router);
        if (values[1] && values[1] !== '') {
          router.navigateByUrl(values[1]);
        } else {
          router.navigate(['/klassenbuch']);
        }
      }),
      map(() => new AddEventSuccessAction({})),
      catchError(error => {
        return observableOf(new AddEventFailAction(error))
      }),)
    )));

  addEventSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.ADD_EVENT_SUCCESS, KlassenbuchActionTypes.DELETE_EVENT_SUCCESS),
    withLatestFrom(this.store.select(klassenbuch.getCalendarDays), this.store.select(klassenbuch.getSelectedDisplayGroups)),
    tap((values) => {
      const messageKey = values[0]['type'] === KlassenbuchActionTypes.DELETE_EVENT_SUCCESS ? 'Die Aufgabe wurde gelöscht' : 'Die Aufgabe wurde gespeichert';
      this.store.dispatch(new AddToastAction({message: messageKey, type: 'success'}))
    }),
    map((values, index) => new GetEventsAction({
      groups: values[2],
      start: values[1][0],
      end: values[1][4]
    })),));

  publishEvent$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.PublishEventAction>(KlassenbuchActionTypes.PUBLISH_EVENT),
    map((action) => {
      let updatedEvent = clone(action.payload);
      updatedEvent['state'] = 'published';
      return new PublishUpdateEventAction(updatedEvent);
    })));

  // TODO: Wayne muess häreha, könnten wir nicht den PUBLISH_UPDATE_EVENT und den UPDATE_EVENT
  // vereinen, in dem wir die beiden events parametrisierbar machen?

  publishUpdateEvent$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.PublishUpdateEventAction>(KlassenbuchActionTypes.PUBLISH_UPDATE_EVENT),
    map(action => action.payload),
    switchMap((event) =>
      this.helloclassApi.updateEvent(<any>event).pipe(
        map((backendEvent: BackendEvent) => new PublishUpdateEventSuccessAction(backendEvent)),
        catchError(error => observableOf(new UpdateEventFailAction(error))),)
    ),));

  updateEvent$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.UPDATE_EVENT),
    map(action => action.payload),
    switchMap((event) =>
      this.helloclassApi.updateEvent(event).pipe( // get shown group id from somewhere
        map((backendEvent: BackendEvent) => new UpdateEventSuccessAction(backendEvent)),
        catchError(error => observableOf(new UpdateEventFailAction(error))),)
    ),));

  updateEventSuccess$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.UPDATE_EVENT_SUCCESS),
    map((action) => {
      let router = this.injector.get(Router);
      router.navigate(['/klassenbuch/events', action.payload.id]);
      return new AddToastAction({message: 'Die Aufgabe wurde gespeichert', type: 'success'});
    })));

  updateEventFail$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.UPDATE_EVENT_FAIL),
    map(() => {
      let router = this.injector.get(Router);
      router.navigate(['/klassenbuch']);
      return new AddToastAction({message: 'Es ist ein Fehler aufgetreten!', type: 'fail'});
    })));

  getEvents$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.GET_EVENTS),
    map(action => action.payload),
    withLatestFrom(this.store.select(klassenbuch.getAppOnline)),
    filter(actionAndNetoworkStatus => actionAndNetoworkStatus[1]),
    map(actionAndNetworkStatus => actionAndNetworkStatus[0]),
    withLatestFrom(
      this.store.select(getAuthUser)
    ),
    filter(values => values[1].id !== -1),
    switchMap((values: [{ groups: Object[], start: string, end: string }, UserData]) =>
      this.helloclassApi.getEvents(values[0].groups, values[1].schoolId, values[0].start, values[0].end).pipe( //get shown group id from somewhere
        map((events: BackendEvent[]) => new GetEventsSuccessAction(events)),
        catchError(error => observableOf(new GetEventsFailAction(error))),)
    ),));

  getEventsFail$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.GET_EVENTS_FAIL),
    map(() => new AddToastAction({message: 'Die Aufgaben konnten nicht geladen werden', type: 'fail'}))));

  getEvent$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.GET_EVENT),
    map(action => action.payload),
    withLatestFrom(this.store.select(klassenbuch.getAppOnline)),
    filter(payloadAndNetwork => payloadAndNetwork[1]),
    map(payloadAndNetwork => payloadAndNetwork[0]),
    switchMap((id: string) => this.helloclassApi.getEvent(id).pipe( // get shown group id from somewhere
      map((event: BackendEvent) => new GetEventSuccessAction(event)),
      catchError(error => observableOf(new GetEventFailAction(error))),)
    ),));

  getEventFail$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.GET_EVENT_FAIL),
    map(() => {
      // handle different status codes
      // currently just route to klassenbuch
      let router = this.injector.get(Router);
      router.navigate(['/klassenbuch']);
      return new AddToastAction({message: 'Es ist ein Fehler aufgetreten!', type: 'fail'});
    })));

  addComment$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.ADD_COMMENT),
    switchMap(action => this.helloclassApi.addComment(action.payload).pipe(
      map(() => {
        this.store.dispatch(new GetEventAction(action.payload.event));
        return new LoadCommentsAction(action.payload.event);
      }),
      catchError(error => observableOf(new AddCommentFailAction(error))),)
    )));

  deleteComment$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.DELETE_COMMENT),
    switchMap(action => this.helloclassApi.deleteComment(action.payload).pipe(
      map(() => {
        this.store.dispatch(new GetEventAction(action.payload.event));
        return new LoadCommentsAction(action.payload.event)
      }),
      catchError(error => observableOf(new DeleteCommentFailAction({}))),)
    )));

  loadComments$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.LoadCommentsAction>(KlassenbuchActionTypes.LOAD_COMMENTS),
    switchMap(action => this.helloclassApi.loadEventComments(action.payload).pipe(
      map((data) => new LoadCommentsSuccessAction(data)),
      catchError(error => observableOf(new LoadCommentsFailAction([]))),)
    )));

  showDeleteEventModal$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.DELETE_EVENT_USER_ACTION),
    map(action => new UiHideMenuAction())));

  confirmDeleteEvent$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.DeleteEventConfirmationAction>(KlassenbuchActionTypes.DELETE_EVENT_CONFIRMATION),
    map(action => new DeleteEventAction(action.payload))));

  deleteEvent$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.DeleteEventAction>(KlassenbuchActionTypes.DELETE_EVENT),
    switchMap(action => this.helloclassApi.deleteEvent(action.payload).pipe(
      map(() => new DeleteEventSuccessAction()),
      tap(() => {
        let router = this.injector.get(Router);
        router.navigate(['/klassenbuch']);
      }),
      catchError(error => observableOf(new DeleteEventFailAction(error))),)
    )));

  loadCommentsSuccessAction$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.LOAD_COMMENTS_SUCCESS),
    withLatestFrom(this.store.select(klassenbuch.getSelectedDate)),
    map((values: [Action, string]) => {
      return new DateSelectedAction({date: values[1], sameWeek: false});
    }),));

  selectedDisplayGroupChange$ = createEffect(() => merge(
    this.actions$.pipe(ofType(KlassenbuchActionTypes.ADD_SELECT_DISPLAY_GROUP)),
    this.actions$.pipe(ofType(KlassenbuchActionTypes.REMOVE_SELECT_DISPLAY_GROUP)))
    .pipe(map(() => new UpdateGroupSettings())));

  updatePage$ = createEffect(() => this.actions$.pipe(
    ofType<UpdateDocumentPageAction>(KlassenbuchActionTypes.UPDATE_DOCUMENT_PAGE),
    withLatestFrom(this.store.select(klassenbuch.getDocumentOrderFilter), this.store.select(klassenbuch.getDocumentQuery)),
    map(([action, orderBy, documentQuery]) => {
        return new GetMyDocumentsAction({page: action.payload, orderBy, query: documentQuery});
      }
    )
  ))

  updateOrderBy$ = createEffect(() => this.actions$.pipe(
    ofType<UpdateDocumentOrderFilterAction>(KlassenbuchActionTypes.UPDATE_DOCUMENT_FILTER),
    withLatestFrom(this.store.select(klassenbuch.getDocumentPage), this.store.select(klassenbuch.getDocumentQuery)),
    map(([action, page, documentQuery]) => {
        return new GetMyDocumentsAction({orderBy: action.payload.key, page, query: documentQuery});
      }
    )
  ))

  updateQuery$ = createEffect(() => this.actions$.pipe(
    ofType<UpdateDocumentQueryAction>(KlassenbuchActionTypes.UPDATE_DOCUMENT_QUERY),
    filter(action => action.payload.length > 2 || action.payload.length === 0),
    withLatestFrom(this.store.select(klassenbuch.getDocumentPage), this.store.select(klassenbuch.getDocumentOrderFilter)),
    map(([action, page, orderBy]) => {
        return new GetMyDocumentsAction({query: action.payload, page, orderBy});
      }
    )
  ))

  getMyDocuments$ = createEffect(() => this.actions$.pipe(
    ofType<GetMyDocumentsAction>(KlassenbuchActionTypes.GET_MY_DOCUMENTS),
    throttle(() => interval(400)),
    switchMap((action) => {
      return this.helloclassApi.getMyDocuments(action.payload).pipe(
        map(docResponse => new GetMyDocumentsSuccessAction(docResponse)),)
    }),
    catchError(error => observableOf(new GetMyDocumentsFailAction())),))

  deleteDocuments$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.DeleteDocumentsAction>(KlassenbuchActionTypes.DELETE_DOCUMENTS),
    switchMap(action => this.helloclassApi.deleteDocuments(action.payload).pipe(
      map(deletedIds => new klassenbuchActions.DeleteDocumentsSuccessAction(deletedIds)),
    ))
  ))

  deleteDocumentsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.DeleteDocumentsSuccessAction>(KlassenbuchActionTypes.DELETE_DOCUMENTS_SUCCESS),
    map(() => new AddToastAction({message: 'Die Datei wurden gelöscht', type: 'success'}))
  ))

  // Offline handling

  getEventOffline$ = createEffect(() => this.actions$.pipe(
    ofType<any>(KlassenbuchActionTypes.GET_EVENT),
    map(action => action.payload),
    withLatestFrom(this.store.select(klassenbuch.getAppOnline)),
    filter(payloadAndNetwork => !payloadAndNetwork[1]),
    map(payloadAndNetwork => payloadAndNetwork[0]),
    switchMap(id => this.offlineStorageServer.getEventFromStorage(id).pipe( // get shown group id from somewhere
      map((event) => new GetEventFromStorageSuccessAction(event)),
      catchError(error => observableOf(new GetEventFailAction(error))),)
    ),));

  getEventsOffline$ = createEffect(() => this.actions$.pipe( // refactor
    ofType<any>(KlassenbuchActionTypes.GET_EVENTS),
    map(action => action.payload),
    withLatestFrom(this.store.select(klassenbuch.getAppOnline)),
    filter(actionAndNetoworkStatus => !actionAndNetoworkStatus[1]),
    withLatestFrom(this.store.select(klassenbuch.getCalendarDays)),
    map((data) => {
      const viewEventsForStore = this.offlineStorageServer.getWeekFromStorage(data[1], data[0][0]['groups']);
      return new GetEventsFromStorageSuccessAction(viewEventsForStore);
    }),));

  prefetchNextWeeks$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.PREFETCH_NEXT_WEEKS),
    map(() => {
      return observableFrom(this.calendarService.getStartAndEndForNextTwoWeeks());
    }),
    concatAll(),
    withLatestFrom( // todo: move to top so that it is only once called
      this.store.select(klassenbuch.getGroups),
      this.store.select(getAuthUser).pipe(
        filter(authUser => authUser.id !== -1))),
    mergeMap((values) => {
      return this.helloclassApi.getEvents(values[1], values[2].schoolId, values[0]['start'], values[0]['end']).pipe(
        map(events => ({events: events, start: values[0]['start']})))
    }),
    map(data => {
      const events = data['events'].map(event => ViewEvent.fromEvent(event))
      this.offlineStorageServer.storeEventsForWeek(data['start'], events);
      return {type: 'NOP', payload: null}
    }),));
  loadAdminMessages$ = createEffect(() => this.actions$.pipe(
    ofType(KlassenbuchActionTypes.LOAD_ADMIN_MESSAGES),
    switchMap(action => this.helloclassApi.loadAdminMessages().pipe(
      map((data) => new LoadAdminMessagesSuccessAction(data)),
      catchError(error => observableOf(new LoadAdminMessagesFailureAction())),)
    )));

  markAdminMessageRead$ = createEffect(() => this.actions$.pipe(
    ofType<klassenbuchActions.MarkAdminMessageReadAction>(KlassenbuchActionTypes.MARK_ADMIN_MESSAGE_READ),
    switchMap(action => this.helloclassApi.markAdminMessageRead(action.payload).pipe(
      map(() => new LoadAdminMessagesAction()),
      catchError(error => observableOf(new LoadAdminMessagesFailureAction())),)
    )));
}
